import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useBlocker,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import {
  useAuth0,
} from '@auth0/auth0-react';
import noop from 'lodash/noop';
import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';
import isNil from 'lodash/isNil';
import mixpanel from 'mixpanel-browser';
import {
  auth0ClientId,
  environment,
  googleMapsApiKey,
} from 'env';
import {
  clearAppAuthState,
  generateTokenItem,
  getAccessTokenFromLocalStorage,
  getIsTokenItemValid,
  setAccessTokenToLocalStorage,
  setHeaders,
} from 'utils/auth';
import {
  emitAuthSessionLoggedOut,
} from 'utils/authSessionSync';
import {
  EventBus,
  EventMap,
} from 'core/EventBus';
import {
  SelectableContext,
} from 'core/context/selectable';
import {
  MediaContext,
} from 'core/context/media';
import {
  VirtualTableInstance,
} from 'core/components/VirtualTable/types';
import {
  isEmptyString,
  notEmptyString,
} from '@populateui/utils';
import {
  ConfirmationDialogContext,
} from 'pages/Dashboard/contexts/confirmationDialog';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import {
  UserSettingsContext,
} from 'pages/Dashboard/contexts/userSettings';
import {
  SidePanelContext,
} from 'pages/Dashboard/contexts/sidePanel';
import {
  FilterInstance,
  FilterObjectType,
} from 'core/Filters/types';
import {
  filterComponentsByType,
} from 'core/Filters/attributes';
import {
  serializeFilterQuery,
} from 'core/Filters/util';
import {
  DateRangeParams,
  useSyncDateRange,
} from 'pages/Dashboard/hooks/url';
import {
  useSnackbar,
  SnackbarKey,
} from 'notistack';
import {
  Props as AlertProps,
  AlertVariant,
} from 'pages/Dashboard/components/Alert';
import {
  Props as NotificationProps,
} from 'core/components/Notification';
import useStorageState from 'core/hooks/storage';
import {
  datadogLogs,
} from '@datadog/browser-logs';
import {
  LoggedInUserInfoDTO,
} from 'dtos';
import {
  FileDragOverlayContext,
} from 'core/context/fileDragOverlay';
import {
  useImpersonation,
} from 'core/context/impersonation';

// @TODO
// If file will exceed 300 lines code,
// let's split it into multiple files under hooks directory,
// where hooks will be grouped together logically

export type NotifyProps = Pick<AlertProps, 'onClose' | 'timeout' | 'dismissible'>
  & {
    message?: string | ReactNode;
    variant?: AlertVariant;
  };

export function useNotify() {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  return useCallback(({
    message = 'try this notification',
    timeout = 10000,
    dismissible = true,
    variant = 'info',
    onClose,
    ...props
  }: Partial<NotifyProps>) => {
    enqueueSnackbar(message, {
      ...props,
      autoHideDuration: timeout,
      variant: 'custom',
      severity: variant,
      dismissible,
      handleClose: (id: SnackbarKey) => {
        closeSnackbar(id);
        onClose?.();
      },
      anchorOrigin: { horizontal: 'center', vertical: 'top' },
    } as NotificationProps);
  }, [closeSnackbar, enqueueSnackbar]);
}

export function useMedia() {
  return useContext(MediaContext);
}

export function useSelectableContext() {
  return useContext(SelectableContext);
}

export function useFileDragOverlayContext() {
  return useContext(FileDragOverlayContext);
}

export function useConfirmDialog() {
  const { openDialog, closeDialog, resetConfirmation } = useContext(ConfirmationDialogContext);
  return {
    confirm: openDialog,
    closeConfirm: closeDialog,
    reset: resetConfirmation,
  };
}

type PromptProps = {
  title?: string;
  message?: string | ReactNode;
  onLeave?: () => void;
  onStay?: () => void;
};

export function usePrompt(when: boolean, {
  title,
  message,
  onLeave,
  onStay,
}: Partial<PromptProps> = {}) {
  // Panels and page filters only change the query string, so they don't prompt
  const blocker = useBlocker(({ currentLocation, nextLocation }) => (
    when && currentLocation.pathname !== nextLocation.pathname
  ));
  const { confirm, closeConfirm } = useConfirmDialog();

  useEffect(() => {
    if (blocker?.state === 'blocked') {
      confirm({
        className: '!w-[32rem]',
        disableBackdropClick: true,
        hideCloseBtn: true,
        title: title ?? 'Leave Page?',
        content:
          isNil(message)
            ? (
              <div className="!mt-0">
                You have unsaved changes. Do you want to leave without saving?
              </div>
            )
            : message,
        confirmText: 'Stay',
        cancelText: 'Leave',
        onConfirm: () => {
          onStay?.();
          blocker.reset?.();
        },
        onClose: () => {
          blocker.proceed?.();
          onLeave?.();
          closeConfirm();
        },
      });
    } else {
      closeConfirm();
    }
  }, [blocker]);
}

export function useUserSettings() {
  const userSettings = useContext(UserSettingsContext);
  return userSettings;
}

/**
 * To add a side panel (details: https://dev.azure.com/populate/PopulateEMR/_wiki/wikis/PopulateEMR.wiki/224/Linkable-side-panel-url-write-mechanism-overview):
 * 1. Write the component, typed `SidePanelComponentProps<YourPanelInfo>`.
 * 2. Register it in `contexts/sidePanel/panelCatalog.ts`; add `url` (e.g. `idParamUrl('fooId')`) to make it linkable,
 *    and render `{slots?.actions}` (Copy link) before its close button.
 * 3. Open it: `openPanel('foo', { fooId })`, with `{ canGoBack: true }` from a parent panel.
 * 4. Keep in-panel state such as the tab in the URL with `usePanelState`.
 */
export function useSidePanel() {
  const {
    activePanel,
    openPanel,
    closePanel,
    goBack,
  } = useContext(SidePanelContext);
  return {
    activePanel,
    openPanel,
    closePanel,
    goBack,
  };
}

export function useLogoutFromApp() {
  const { logout: auth0Logout } = useAuth0();
  return useCallback(async (options?: { returnTo?: string }) => {
    emitAuthSessionLoggedOut();
    clearAppAuthState();
    await auth0Logout({
      clientId: auth0ClientId,
      logoutParams: {
        returnTo: options?.returnTo ?? window.location.origin,
      },
    });
  }, [auth0Logout]);
}

export function useAuth() {
  const {
    isAuthenticated,
    getAccessTokenSilently,
    isLoading,
    loginWithRedirect,
    handleRedirectCallback,
    user,
  } = useAuth0();
  const logout = useLogoutFromApp();

  const isGoogleOAuth2 = useMemo(() => {
    const provider = user?.sub?.split('|')[0];
    return provider === 'google-oauth2';
  }, [user]);

  const issueAccessToken = useCallback(async (): Promise<string | null> => {
    const cachedTokenItem = getAccessTokenFromLocalStorage();
    const cachedTokenIsExpired = !getIsTokenItemValid(cachedTokenItem) && isAuthenticated;

    try {
      const token = await (cachedTokenIsExpired
        ? getAccessTokenSilently()
        : Promise.resolve(cachedTokenItem.token)
      );

      if (cachedTokenIsExpired) {
        setAccessTokenToLocalStorage(generateTokenItem(token));
      }

      setHeaders(token);
      return token;
    } catch (e) {
      console.warn('[Auto Logout] Failed to issue access token.', e);
      logout();
    }

    return null;
  }, [isAuthenticated, getAccessTokenSilently, logout]);

  return {
    issueAccessToken,
    isAuthenticated,
    isLoading,
    logout,
    loginWithRedirect,
    handleRedirectCallback,
    getAccessTokenSilently,
    user,
    isGoogleOAuth2,
  };
}

type EventSubscription<T extends EventMap> = {
  channel: EventBus<T>;
  event: keyof T;
};

type UseTableResetProps<T extends EventMap> = {
  changeSubscription?: EventSubscription<T>;
};

export function useTableReset<T extends EventMap>(
  { changeSubscription }: UseTableResetProps<T> = {},
) {
  const {
    channel,
    event,
  } = changeSubscription ?? {};
  const tableInstanceRef = useRef<VirtualTableInstance>(null);

  useEffect(() => {
    const unsubscribe = (!isEmpty(changeSubscription) && notEmptyString(event))
      ? channel?.on?.(event as keyof T, (() => {
        tableInstanceRef?.current?.refetch?.();
      }) as T[keyof T])
      : noop;
    return () => {
      unsubscribe?.();
    };
  }, []);

  const reset = () => {
    const { pagination, setPageIndex } = tableInstanceRef?.current ?? {};
    if (pagination?.pageIndex !== 0 && isFunction(setPageIndex)) {
      setPageIndex(0);
    }
  };

  return {
    tableInstanceRef,
    reset,
  };
}

export function useFilterQuery<T>(
  objectType: FilterObjectType,
  useStorage: boolean = false,
) {
  const filterInstanceRef = useRef<FilterInstance>(null);

  const { settings } = useUserSettings();
  const components = filterComponentsByType(objectType);

  const defaultValue = serializeFilterQuery(
    settings[`${objectType}Filters`],
    components,
  ) as T;
  const storageState = useStorageState<T>(`${objectType}Filters`, defaultValue);
  const localState = useState<T>(defaultValue);
  const [filterQuery, setFilterQuery] = useStorage
    ? storageState
    : localState;

  return {
    filterQuery,
    setFilterQuery,
    filterInstanceRef,
  };
}

export function useFilterQueryWithDateRange<T>(
  objectType: FilterObjectType,
  dateRangeParams?: DateRangeParams,
  useStorage: boolean = false,
) {
  const baseQuery = useFilterQuery<T>(objectType, useStorage);
  const [dateRange, setDateRange] = useSyncDateRange(dateRangeParams ?? {});

  return {
    ...baseQuery,
    dateRange,
    setDateRange,
  };
}

export function useGoogleMaps() {
  useEffect(() => {
    if (isEmptyString(googleMapsApiKey)) {
      console.error('Missing Google Maps API key.');

      return undefined;
    }

    const script = document.createElement('script');
    script.id = 'google-maps-script';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places&loading=async`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [googleMapsApiKey]);
}

export function useDatadog() {
  const { isGoogleOAuth2, user } = useAuth();
  const setDatadogUser = (userInfo: LoggedInUserInfoDTO | null) => {
    if (!isNil(userInfo)) {
      const { userId, email, formattedNames } = userInfo?.user ?? {};
      datadogLogs.setUser({
        id: userId?.toString(),
        name: formattedNames?.fullName ?? '',
        email: email ?? '',
        ...(isGoogleOAuth2 ? {
          isImpersonated: isGoogleOAuth2,
          impersonatedBy: user?.email,
        } : {}),
      });
    }
  };
  return { setDatadogUser };
}

// This prop is for overriding to enable mixpanel tracking
export function useMixpanelTrack(isEnabled: boolean = false) {
  const enabled = isEnabled || environment === 'prod';

  const {
    isAuthenticated,
    isGoogleOAuth2,
    user: authUser,
  } = useAuth();

  const { isImpersonating } = useImpersonation();

  const isPortal = isAuthenticated && isGoogleOAuth2 && !isImpersonating;
  const userInfo = useUserInfo(!isPortal);
  const { user, organization } = userInfo ?? {};

  return enabled ? (eventName: string, properties?: Record<string, any>) => {
    mixpanel.track(eventName, {
      ...(properties ?? {}),
      userTitle: user?.title,
      userId: user?.userId,
      orgId: organization?.organizationId,
      env: environment,
      ...(isPortal ? {
        impersonatedBy: authUser?.email,
      } : {}),
    });
  } : noop;
}

export function useNavigateToBack() {
  const navigate = useNavigate();
  return (fallback?: string) => {
    const historyIndex = window.history.state?.idx;
    // auth pushes state to history when opening
    if (historyIndex === 0) {
      navigate(fallback ?? '/encounters');
    } else {
      navigate(-1);
    }
  };
}

export function useCloseOnRouteChange(onClose: () => void) {
  const { pathname } = useLocation();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    onCloseRef.current();
  }, [pathname]);
}
